import React from 'react';
import { AdminFormFieldRow } from './AdminFormFieldRow';
import {
  ACCOUNT_TYPE_LABEL_JA,
  ACCOUNT_TYPE_OPTIONS,
  BANK_TYPE_LABEL_JA,
  BANK_TYPE_OPTIONS,
  type AccountTypeCode,
  type BankTypeCode,
} from '../../../employee/fillingForms/chat/bankAccountDetails/bankAccountDetailsTypes';
import { formatAccountType, formatBankType } from '../../../employee/fillingForms/chat/bankAccountDetails/bankAccountDetailsDisplay';
import { BANK_ACCOUNT_SECTION_TITLE } from './bankAccountDetailsAdminLayout';
import './ProfileSectionPreview.css';

function formatBankInstitutionDisplay(bankName: string, bankType: string, bankTypeOther: string): string {
  const name = bankName.trim();
  const type = bankType.trim().toLowerCase();
  if (!name && !type) return '';
  if (type === 'other' && bankTypeOther.trim()) {
    const typePart = `その他（${bankTypeOther.trim()}）`;
    return name ? `${name}（${typePart}）` : typePart;
  }
  if (type) {
    const typeLabel = formatBankType(bankType);
    return name ? `${name}（${typeLabel}）` : typeLabel;
  }
  return name;
}

function fieldChanged(
  values: Record<string, string>,
  snapshotValues: Record<string, string> | undefined,
  key: string
): boolean {
  if (!snapshotValues || !(key in snapshotValues)) return false;
  return (values[key] ?? '') !== (snapshotValues[key] ?? '');
}

export function BankAccountDetailsAdminFieldEditor({
  values,
  editing,
  onChange,
  snapshotValues,
}: {
  values: Record<string, string>;
  editing: boolean;
  onChange: (key: string, value: string) => void;
  snapshotValues?: Record<string, string>;
}) {
  const bankName = values.bankName ?? '';
  const bankType = values.bankType ?? '';
  const bankTypeOther = values.bankTypeOther ?? '';
  const branchName = values.branchName ?? '';
  const accountType = values.accountType ?? '';
  const accNum = values.accNum ?? '';
  const nameKana = values.nameKana ?? '';
  const showBankTypeOther = bankType.trim().toLowerCase() === 'other';

  const bankNameDisplay = formatBankInstitutionDisplay(bankName, bankType, bankTypeOther);

  const bankNameSnap =
    snapshotValues != null
      ? formatBankInstitutionDisplay(
          snapshotValues.bankName ?? '',
          snapshotValues.bankType ?? '',
          snapshotValues.bankTypeOther ?? ''
        )
      : undefined;

  const bankNameChanged =
    snapshotValues != null &&
    (bankNameDisplay !== (bankNameSnap ?? '') ||
      fieldChanged(values, snapshotValues, 'bankName') ||
      fieldChanged(values, snapshotValues, 'bankType') ||
      fieldChanged(values, snapshotValues, 'bankTypeOther'));

  return (
    <div className="profile-section-preview__content">
      <h3 className="profile-section-preview__section-title">{BANK_ACCOUNT_SECTION_TITLE}</h3>

      <AdminFormFieldRow
        label="金融機関店名"
        value={bankNameDisplay}
        editing={editing}
        changed={bankNameChanged}
        compareValue={bankNameSnap}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input
            className="profile-section-preview__input"
            value={bankName}
            placeholder="金融機関名"
            onChange={(e) => onChange('bankName', e.target.value)}
          />
          <select
            className="profile-section-preview__input"
            value={bankType}
            onChange={(e) => {
              const next = e.target.value;
              onChange('bankType', next);
              if (next !== 'other') {
                onChange('bankTypeOther', '');
              }
            }}
          >
            <option value="">種類を選択</option>
            {BANK_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {BANK_TYPE_LABEL_JA[opt.value as BankTypeCode]}
              </option>
            ))}
          </select>
        </div>
      </AdminFormFieldRow>

      {showBankTypeOther && (
        <AdminFormFieldRow
          label="金融機関の種類（その他）"
          value={bankTypeOther}
          editing={editing}
          changed={fieldChanged(values, snapshotValues, 'bankTypeOther')}
          compareValue={snapshotValues?.bankTypeOther}
        >
          <input
            className="profile-section-preview__input"
            value={bankTypeOther}
            onChange={(e) => onChange('bankTypeOther', e.target.value)}
          />
        </AdminFormFieldRow>
      )}

      <AdminFormFieldRow
        label="支店名"
        value={branchName}
        editing={editing}
        changed={fieldChanged(values, snapshotValues, 'branchName')}
        compareValue={snapshotValues?.branchName}
      >
        <input
          className="profile-section-preview__input"
          value={branchName}
          onChange={(e) => onChange('branchName', e.target.value)}
        />
      </AdminFormFieldRow>

      <AdminFormFieldRow
        label="預金の種類"
        value={formatAccountType(accountType)}
        editing={editing}
        changed={fieldChanged(values, snapshotValues, 'accountType')}
        compareValue={
          snapshotValues?.accountType != null ? formatAccountType(snapshotValues.accountType) : undefined
        }
      >
        <select
          className="profile-section-preview__input"
          value={accountType}
          onChange={(e) => onChange('accountType', e.target.value)}
        >
          <option value="">—</option>
          {ACCOUNT_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {ACCOUNT_TYPE_LABEL_JA[opt.value as AccountTypeCode]}
            </option>
          ))}
        </select>
      </AdminFormFieldRow>

      <AdminFormFieldRow
        label="口座番号"
        value={accNum}
        editing={editing}
        changed={fieldChanged(values, snapshotValues, 'accNum')}
        compareValue={snapshotValues?.accNum}
      >
        <input
          className="profile-section-preview__input"
          value={accNum}
          inputMode="numeric"
          maxLength={7}
          onChange={(e) => onChange('accNum', e.target.value.replace(/\D/g, '').slice(0, 7))}
        />
      </AdminFormFieldRow>

      <AdminFormFieldRow
        label="名義人（カタカナ）"
        value={nameKana}
        editing={editing}
        changed={fieldChanged(values, snapshotValues, 'nameKana')}
        compareValue={snapshotValues?.nameKana}
      >
        <input
          className="profile-section-preview__input"
          value={nameKana}
          onChange={(e) => onChange('nameKana', e.target.value)}
        />
      </AdminFormFieldRow>
    </div>
  );
}

export default BankAccountDetailsAdminFieldEditor;
